import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { cGradeOf } from '@shared/levels';
import { api } from '../api/client.js';
import { CandidateCompare } from './CandidateCompare.js';
import { IncumbentCard } from './IncumbentCard.js';
import { PersonCard, levelLabel } from '../pages/PersonCard.js';
import { retireMark } from './OrgFlow.js';
import { VacancyCandidates } from './VacancyCandidates.js';
import type {
  Position,
  PositionHolder,
  PositionQualification,
  QualificationPath,
} from '@shared/types';

/**
 * รายละเอียดของหนึ่งอัตรา — แสดงแทนที่บัญชีรายชื่อในหน้าต่างเดียวกัน
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมแทนที่ ไม่ใช่ซ้อนอีกชั้น
 * ────────────────────────────────────────────────────────────────
 *
 * หน้าต่างซ้อนหน้าต่างทำให้ผู้ใช้ต้องกดปิดสองครั้งกว่าจะกลับผัง และบนจอเล็ก
 * กล่องชั้นในจะเล็กลงจนอ่านไม่ไหว การสลับเนื้อในหน้าต่างเดิมพร้อมปุ่ม "กลับ"
 * ทำให้เส้นทางกลับมีทางเดียวและมองเห็นตลอด
 *
 * ────────────────────────────────────────────────────────────────
 *  ทุกกล่องยุบได้ และสรุปให้เห็นตั้งแต่ตอนยุบ
 * ────────────────────────────────────────────────────────────────
 *
 * ⚠ หัวกล่องที่ยุบอยู่ต้องบอกสาระสำคัญไว้ด้วยเสมอ ไม่ใช่มีแต่ชื่อกล่อง
 *   กล่องที่ยุบแล้วไม่บอกอะไรเลย บังคับให้ผู้ใช้กดเปิดทุกกล่องเพื่อหาว่าอันไหนมีของ
 *   ซึ่งแย่กว่าการไม่ให้ยุบตั้งแต่แรก
 *
 * ⚠ กล่องที่ต้องยิงคำขอ (ประวัติรายบุคคล · คุณสมบัติ) จะไม่โหลดจนกว่าจะถูกเปิด
 *   ถ้าโหลดทั้งหมดตอนเปิดหน้า การกดดูอัตราหนึ่งอัตราจะยิงสามคำขอทุกครั้ง
 *   ทั้งที่ส่วนใหญ่ผู้ใช้ดูแค่ว่าใครครองอยู่แล้วกดกลับ
 */
export function PositionDetail({ p }: { p: Position }) {
  const who = p.incumbent;
  const mark = retireMark(who ?? null);
  const c = cGradeOf(p.level);
  /**
   * รหัสบุคคลที่กำลังเทียบกันอยู่ — null คือยังไม่ได้กดเทียบ
   *
   * ⚠ สลับเนื้อในหน้าต่างเดิม ไม่ใช่เปิดหน้าต่างซ้อน ด้วยเหตุผลเดียวกับที่หัวไฟล์นี้
   *   และตารางเทียบต้องใช้ความกว้างทั้งหน้าต่าง ถ้าแสดงในกล่องยุบได้ของชั้นผู้เข้าเงื่อนไข
   *   สามคอลัมน์จะถูกบีบจนอ่านไม่ได้
   */
  const [compare, setCompare] = useState<string[] | null>(null);

  if (compare) {
    return <CandidateCompare pos={p} ids={compare} onBack={() => setCompare(null)} />;
  }

  return (
    <div className="pdt">
      {/* ────────── ผู้ครองปัจจุบัน ────────── */}
      <Fold
        title={p.vacant ? 'สถานะอัตรา' : 'ผู้ครองปัจจุบัน'}
        summary={
          who ? (
            <>
              <b>{who.name}</b>
              {mark && <span className={`pdt-chip is-${mark.tone}`}>{mark.label}</span>}
            </>
          ) : (
            <>
              <b className="is-vacant">ว่าง</b>
              <span className="pdt-chip is-vacant">
                {p.vacantMonths === null ? 'ไม่ทราบว่าว่างตั้งแต่เมื่อไร' : `ว่างมาแล้ว ${duration(p.vacantMonths)}`}
              </span>
              {/* หัวกล่องที่ยุบอยู่ต้องบอกสาระสำคัญ — "ว่างแต่มีผู้รักษาการ" ต่างจาก "ว่างเปล่า" มาก */}
              {p.acting && <span className="pdt-chip is-acting">รักษาการ · {p.acting.name}</span>}
            </>
          )
        }
        defaultOpen
      >
        {who ? (
          <>
            {/* ⚠ ตารางสองคอลัมน์เดิมถูกแทนด้วยแกนเส้นทางราชการ (IncumbentCard)
                ตามที่ผู้ใช้เลือกจากสามแบบที่เสนอ เหตุผลของรูปแบบอยู่ในหัวไฟล์นั้น
                ค่าทุกช่องของตารางเดิมยังอยู่ครบในแกน ไม่ได้หายไปสักช่อง —
                ชื่อ อายุ ตำแหน่ง อยู่บนแถบตัวตน · วันบรรจุ วันครองตำแหน่ง วันเกษียณ
                ปีงบที่เกษียณ และเวลาที่เหลือ อยู่บนแกน · ผ่านเกณฑ์เลื่อนระดับเป็นป้ายบนแถบตัวตน */}
            <IncumbentCard p={p} />

            {/* ข้อมูลประกอบ (การศึกษา · อบรม · ประเมิน · เครื่องราชฯ …) ยังใช้ PersonCard ตัวเดิม
                ⚠ สั่ง hideCareer ด้วย เพราะแกนด้านบนวาดประวัติชุดเดียวกันไปแล้ว
                  ถ้าไม่ตัด จะเห็นประวัติเดียวกันสองแบบในจอเดียวแล้วต้องเดาว่าอันไหนถูก

                ⚠ แลกมาด้วยคำขอเพิ่มหนึ่งครั้งต่อการเปิดดูหนึ่งอัตรา — เดิมกล่องนี้เป็น
                  Fold แบบ lazy ที่ยิงคำขอเฉพาะตอนกด ตอนนี้ยิงทุกครั้งที่เปิดอัตรา
                  ผลกระทบถูกจำกัดด้วยแคชของ react-query และ IncumbentCard ใช้ queryKey
                  ตัวเดียวกัน จึงยังเป็นคำขอเดียว ไม่ใช่สอง

                ⚠ hideActions — ปุ่ม "บันทึกเป็น PDF/พิมพ์" ของประวัติรายบุคคลถูกตัดออก
                  เพราะหัวแผงนี้มีปุ่มส่งออกทั้งอัตราอยู่แล้ว ซึ่งได้เอกสารที่ครบกว่า
                  (มีข้อมูลอัตรา ผู้รักษาการ และผู้เคยครองอยู่ในไฟล์เดียวกัน) */}
            {who.personaId ? (
              <PersonCard personaId={who.personaId} embedded hideIdentity hideCareer hideActions />
            ) : (
              <p className="pdt-note">
                {/* อัตราที่มีผู้ครองแต่ไม่มีรหัสบุคคล เชื่อกับตารางประวัติไม่ได้ — ปัญหาคุณภาพข้อมูลที่ต้องเห็น ไม่ใช่ซ่อน */}
                อัตรานี้มีผู้ครองแต่ข้อมูลไม่มีรหัสบุคคล จึงเชื่อมกับประวัติการดำรงตำแหน่งไม่ได้
              </p>
            )}
          </>
        ) : (
          <>
            <dl className="pdt-dl">
              <F k="สถานะ" v="ว่าง" tone="alert" />
              <F k="ว่างตั้งแต่" v={thaiDate(p.vacantSince)} />
              <F k="ว่างมาแล้ว" v={p.vacantMonths === null ? null : duration(p.vacantMonths)} tone="alert" />
              <F k="ผู้ครองคนก่อนหน้า" v={p.lastHolder?.name ?? null} />
              <F k="วันสุดท้ายที่ครอง" v={thaiDate(p.lastHolder?.until ?? null)} />
              {/* ⚠ reason เป็น null แปลว่า "ไฟล์ต้นทางไม่มีข้อมูลนี้" ไม่ใช่ "ไม่ทราบสาเหตุ" */}
              <F k="เหตุที่พ้นจากตำแหน่ง" v={p.lastHolder?.reason ?? null} />
              {/* ⚠ เขียน "ผู้รักษาการ" ไม่ใช่ "ผู้ครอง" — คนละฐานอำนาจกัน
                  และต้องบอกตำแหน่งหลักของเขาด้วย ไม่งั้นอ่านไม่ออกว่าคนนี้มาจากไหน */}
              <F k="ผู้รักษาการในตำแหน่ง" v={p.acting?.name ?? null} />
              <F k="ตำแหน่งหลักของผู้รักษาการ" v={p.acting ? [p.acting.homeTitle, p.acting.homeLevel].filter(Boolean).join(' · ') || null : null} />
              <F k="รักษาการตั้งแต่" v={thaiDate(p.acting?.since ?? null)} />
            </dl>
            {!p.lastHolder && (
              <p className="pdt-note">
                ยังไม่ทราบผู้ครองคนก่อนหน้า — ชุดข้อมูลที่ได้จาก DPIS ไม่มีฟิลด์นี้มาด้วย
              </p>
            )}
          </>
        )}
      </Fold>

      {/* ────────── ข้อมูลอัตรา ────────── */}
      <Fold
        title="ข้อมูลอัตรา"
        summary={
          <>
            <b>{levelLabel(p.level)}</b>
            {c && <span className="pdt-chip is-c">{c}</span>}
            <span className="pdt-dim">{p.posNo ? `เลขที่ ${p.posNo}` : 'ไม่มีเลขที่ตำแหน่ง'}</span>
          </>
        }
        defaultOpen
      >
        <dl className="pdt-dl">
          <F k="เลขที่ตำแหน่ง" v={p.posNo} />
          <F k="ชื่อตำแหน่ง" v={p.title} />
          {p.assignment && <F k="หน้าที่ที่ได้รับมอบหมาย" v={p.assignment} />}
          <F k="สายงาน" v={p.line} />
          <F k="ระดับ" v={p.level ? `${levelLabel(p.level)}${c ? ` · เทียบ${c}` : ''}` : null} />
          <F k="ประเภทบุคลากร" v={p.category} />
          <F k="กลุ่มตำแหน่งตามต้นทาง" v={p.positionGroup} />
          <F k="สถานะ" v={p.vacant ? 'ว่าง' : 'มีผู้ครอง'} tone={p.vacant ? 'alert' : undefined} />
          <F k="สังกัด" v={p.orgPath.length > 0 ? p.orgPath.join(' › ') : p.unit} wide />
        </dl>
      </Fold>

      {/* ────────── คุณสมบัติของผู้ที่จะครองตำแหน่งนี้ได้ ────────── */}
      <Fold title="คุณสมบัติของผู้ที่จะครองตำแหน่งนี้ได้" summary={<span className="pdt-dim">เกณฑ์ตามมาตรฐานกำหนดตำแหน่ง และเกณฑ์ที่มากับข้อมูล</span>} lazy>
        <Qualification posKey={p.posId} />
      </Fold>

      {/* ────────── ผู้ที่เคยครองอัตรานี้ ────────── */}
      <Fold
        title="ผู้ที่เคยครองอัตรานี้"
        summary={
          p.lastHolder ? (
            <>
              <span className="pdt-dim">ล่าสุด</span>
              <b>{p.lastHolder.name}</b>
            </>
          ) : (
            <span className="pdt-dim">ประวัติการครองอัตรานี้</span>
          )
        }
        lazy
      >
        <Holders p={p} />
      </Fold>

      {/* ────────── ผู้ที่เข้าเงื่อนไข ──────────
          เดิมขึ้นเฉพาะอัตราว่าง ด้วยเหตุผลว่ารายชื่อในอัตราที่มีคนครองจะอ่านได้ว่า
          "คนกลุ่มนี้จะมาแทน" ทั้งที่ไม่มีใครกำลังจะพ้นตำแหน่ง

          เจ้าของระบบสั่งให้แสดงทุกอัตรา เพราะการวางแผนอัตรากำลังต้องตอบล่วงหน้าว่า
          "ถ้าอัตรานี้ว่างลง ใครรับต่อได้" โดยเฉพาะอัตราที่ผู้ครองจะเกษียณใน 5 ปี
          ซึ่งเป็นรายการที่เปิดจากตัวเลข "เกษียณอีก 5 ปี" บนการ์ดโดยตรง

          ⚠ ข้อกังวลเดิมยังจริงอยู่ จึงแก้ที่ถ้อยคำ ไม่ใช่ซ่อนรายการ —
            อัตราที่มีผู้ครองใช้คำว่า "เมื่ออัตรานี้ว่างลง" กำกับทุกครั้ง
            ห้ามตัดคำนั้นออกให้เหลือหัวข้อเดียวกับอัตราว่าง

          ⚠ ตรวจแล้วว่าผู้ครองคนปัจจุบันไม่เคยโผล่ในรายชื่อของอัตราตัวเอง
            (สุ่ม 400 อัตราที่มีผู้ครอง พบ 0 ราย) — ชั้นย้ายแนวราบกันด้วย fromUnit === toUnit
            ส่วนชั้นเกณฑ์มาตรฐานกันด้วยระดับ ถ้าวันหนึ่งสองเงื่อนไขนั้นเปลี่ยน ต้องมากรองที่นี่ */}
      <Fold
        title={p.vacant ? 'ผู้ที่เข้าเงื่อนไขจะครองอัตรานี้ได้' : 'ผู้ที่เข้าเงื่อนไขจะครองอัตรานี้ได้เมื่อว่างลง'}
        summary={
          <span className="pdt-dim">
            {p.vacant
              ? 'ผู้ผ่านเกณฑ์ระยะเวลา และผู้ที่ย้ายแนวราบมาได้'
              : 'ยังไม่ว่าง — รายชื่อนี้ใช้วางแผนล่วงหน้า ไม่ใช่ผู้ที่จะมาแทน'}
          </span>
        }
        lazy
      >
        <VacancyCandidates pos={p} onCompare={setCompare} />
      </Fold>

    </div>
  );
}

/* ================================================================
   ผู้ที่เคยครองอัตรานี้
   ================================================================ */

/**
 * ⚠ สองที่มา ต้องแสดงทั้งคู่ ห้ามเลือกมาอันเดียว
 *
 *   lastHolder  จากทะเบียนตำแหน่ง — มีครบทุกอัตราว่าง (733/733) แต่บอกได้แค่คนสุดท้าย
 *   holders     จากตารางประวัติที่เชื่อด้วย posId — ย้อนได้หลายคน แต่มีแค่ 92% ของอัตรา
 *
 * ถ้าแสดงแต่ holders อัตราที่ประวัติเชื่อไม่ติดจะขึ้นว่า "ไม่มีข้อมูล" ทั้งที่ทะเบียน
 * บอกชื่อคนสุดท้ายไว้อยู่แล้ว ซึ่งอ่านได้ว่าอัตรานี้ไม่เคยมีใครครอง — ไม่จริง
 */
function Holders({ p }: { p: Position }) {
  const q = useQuery({
    queryKey: ['position-holders', p.posId],
    queryFn: () => api.positionHolders(p.posId),
  });

  if (q.isPending) return <p className="state">กำลังโหลดประวัติผู้ครองอัตรานี้…</p>;
  if (q.isError) return <p className="state error">{String(q.error)}</p>;

  const rows = q.data?.holders ?? [];
  const last = q.data?.lastHolder ?? null;

  return (
    <div className="pdt-holders">
      {last && (
        <dl className="pdt-dl">
          <F k="ผู้ครองคนสุดท้ายตามทะเบียน" v={last.name} />
          <F k="วันสุดท้ายที่ครอง" v={thaiDate(last.until)} />
          {/* ⚠ reason เป็น null แปลว่าไฟล์ต้นทางไม่มีข้อมูลนี้ ไม่ใช่ "ไม่ทราบสาเหตุ" */}
          <F k="เหตุที่พ้นจากตำแหน่ง" v={last.reason} wide />
        </dl>
      )}

      {rows.length === 0 ? (
        <p className="pdt-note">
          {last
            ? 'ประวัติการดำรงตำแหน่งในระบบยังเชื่อมกับอัตรานี้ไม่ได้ จึงย้อนได้แค่ผู้ครองคนสุดท้ายจากทะเบียนข้างบน'
            : 'ประวัติการดำรงตำแหน่งในระบบยังเชื่อมกับอัตรานี้ไม่ได้ และทะเบียนตำแหน่งก็ไม่มีชื่อผู้ครองคนก่อน'}
        </p>
      ) : (
        <>
          <h6>จากตารางประวัติการดำรงตำแหน่ง — เรียงใหม่ก่อนเก่า</h6>
          <ol className="pdt-holders-list">
            {rows.map((h, i) => (
              <HolderRow key={`${h.personaId}-${h.startDate ?? i}`} h={h} />
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

function HolderRow({ h }: { h: PositionHolder }) {
  return (
    <li className={h.current ? 'is-current' : undefined}>
      <span className="who">
        <b>{h.name ?? `ไม่ทราบชื่อ (รหัส ${h.personaId})`}</b>
        {h.current && <em className="is-now">ครองอยู่ปัจจุบัน</em>}
        {h.acting && <em className="is-acting">รักษาการ</em>}
        {h.former && !h.current && <em>พ้นจากราชการแล้ว</em>}
      </span>

      <span className="when">
        {thaiDate(h.startDate) ?? 'ไม่ทราบวันเริ่ม'} — {h.endDate ? thaiDate(h.endDate) : (h.current ? 'ปัจจุบัน' : 'ไม่ทราบวันสิ้นสุด')}
      </span>

      <span className="meta">
        {[h.title, h.level].filter(Boolean).join(' · ') || '—'}
        {/* ⚠ ไม่มีเลขที่คำสั่ง = ไฟล์ต้นทางไม่ส่งมา ไม่ใช่ไม่มีคำสั่งแต่งตั้ง
            แถวที่เติมย้อนหลังจากข้อมูลผู้พ้นราชการไม่มีสองช่องนี้ทั้งชุด */}
        {h.orderNo ? ` · คำสั่งที่ ${h.orderNo}${h.orderDate ? ` ลง ${thaiDate(h.orderDate)}` : ''}` : ' · ไฟล์ต้นทางไม่มีเลขที่คำสั่ง'}
      </span>
    </li>
  );
}

/* ================================================================
   คุณสมบัติ — สองชั้น สองที่มา ต้องแยกป้ายให้ชัด
   ================================================================ */

function Qualification({ posKey }: { posKey: string }) {
  const q = useQuery({
    queryKey: ['position-qualification', posKey],
    queryFn: () => api.positionQualification(posKey),
  });

  if (q.isPending) return <p className="state">กำลังโหลดเกณฑ์ของตำแหน่ง…</p>;
  if (q.isError) return <p className="state error">{String(q.error)}</p>;

  const d = q.data as PositionQualification;

  return (
    <div className="pdt-qual">
      <Standard d={d} />
      <FileRequirement d={d} />
    </div>
  );
}

function Standard({ d }: { d: PositionQualification }) {
  if (!d.standard) {
    return (
      <section className="pdt-src">
        <h5>มาตรฐานกำหนดตำแหน่ง <span className="pdt-tag is-rule">ชั้นกฎ</span></h5>
        {/* ⚠ "ยังไม่มีมาตรฐานในระบบ" ไม่ใช่ "ตำแหน่งนี้ไม่มีเงื่อนไข" — ต้องเขียนให้ต่างกัน */}
        <p className="pdt-note">{d.standardMissing ?? 'ยังไม่มีมาตรฐานกำหนดตำแหน่งของระดับนี้ในระบบ'}</p>
      </section>
    );
  }

  const s = d.standard;

  return (
    <section className="pdt-src">
      <h5>
        มาตรฐานกำหนดตำแหน่ง <span className="pdt-tag is-rule">ชั้นกฎ</span>
      </h5>

      <dl className="pdt-dl">
        <F k="รหัสมาตรฐาน" v={s.code} />
        <F k="ชื่อตำแหน่งตามมาตรฐาน" v={s.positionTitle} />
        <F k="ประเภท · ระดับ" v={`${s.category} · ${s.level}`} />
        <F k="สายงานที่บังคับใช้" v={s.appliesToLines === 'all' ? 'ทุกสายงานในระดับนี้' : s.appliesToLines.join(' · ')} wide />
        <F k="เอกสารต้นทาง" v={`${s.source.title}${s.source.ref ? ` (${s.source.ref})` : ''}`} wide />
      </dl>

      <h6>เส้นทางที่ทำให้เข้าเกณฑ์ — ผ่านข้อใดข้อหนึ่งก็พอ</h6>
      <ol className="pdt-paths">
        {s.paths.map((path) => (
          <Path key={path.id} p={path} />
        ))}
      </ol>

      {s.additionalRequirements.length > 0 && (
        <>
          <h6>เงื่อนไขเพิ่มเติม — ต้องผ่านทุกข้อ</h6>
          <ol className="pdt-paths is-all">
            {s.additionalRequirements.map((path) => (
              <Path key={path.id} p={path} />
            ))}
          </ol>
        </>
      )}
    </section>
  );
}

function Path({ p }: { p: QualificationPath }) {
  return (
    <li>
      <b>
        <span className="pdt-no">{p.id}</span>
        {p.label}
      </b>
      <span className="pdt-dim">
        {p.minMonths === null ? 'ไม่กำหนดระยะเวลาขั้นต่ำ' : `อย่างน้อย ${duration(p.minMonths)}`}
        {p.mustHoldNow && ' · ต้องกำลังดำรงตำแหน่งอยู่ ณ วันนี้'}
      </span>
      {p.counts.length > 0 && (
        <span className="pdt-counts">
          {p.counts.map((c, i) => (
            <em key={`${c.category}-${c.level}-${i}`}>{c.category} {c.level}</em>
          ))}
        </span>
      )}
      {/* ⚠ ข้อที่ระบบตรวจเองไม่ได้ ต้องบอกไว้ ไม่งั้นผู้ใช้จะคิดว่าระบบตรวจครบทุกข้อแล้ว */}
      {p.manualCheck && <span className="pdt-flag">ข้อนี้ระบบตรวจอัตโนมัติไม่ได้ ต้องให้เจ้าหน้าที่ยืนยัน</span>}
      {p.ambiguity && <span className="pdt-flag is-amb">ข้อความในเอกสารกำกวม: {p.ambiguity}</span>}
    </li>
  );
}

function FileRequirement({ d }: { d: PositionQualification }) {
  const r = d.fileRequirement;

  return (
    <section className="pdt-src">
      <h5>
        เกณฑ์ที่มากับข้อมูล <span className="pdt-tag is-data">ชั้นข้อมูล</span>
      </h5>

      {!r ? (
        <p className="pdt-note">ไฟล์ต้นทางไม่มีแถวเกณฑ์ของชื่อตำแหน่งนี้</p>
      ) : (
        <>
          <dl className="pdt-dl">
            <F k="ชื่อตำแหน่ง" v={r.positionTitle} />
            <F k="กลุ่มตำแหน่ง" v={r.positionGroup} />
            <F
              k="ประสบการณ์ขั้นต่ำ"
              v={r.requiredExperienceYears === null ? null : `${r.requiredExperienceYears} ปี`}
            />
            <F
              k="คะแนนภาวะผู้นำเป้าหมาย"
              v={r.targetLeadershipScore === null ? null : `${r.targetLeadershipScore} จาก 5`}
            />
          </dl>

          <Chips label="วุฒิการศึกษาที่ต้องการ" items={r.requiredEducation} />
          <Chips label="สมรรถนะที่ต้องการ" items={r.requiredCompetencies} />

          {r.responsibilities.length > 0 && (
            <>
              <h6>หน้าที่และความรับผิดชอบ</h6>
              <ul className="pdt-bullets">
                {r.responsibilities.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}

/** รายการสั้น ๆ แบบชิป — ไม่มีของก็ต้องขึ้นหัวข้อไว้ ไม่ใช่ซ่อนทั้งหัวข้อ
    เพราะ "ไม่มีข้อมูล" กับ "ไม่มีหัวข้อนี้" คนละเรื่องสำหรับคนที่กำลังตรวจข้อมูล */
function Chips({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="pdt-chips">
      <h6>{label}</h6>
      {items.length === 0 ? (
        <p className="pdt-note">ไฟล์ต้นทางไม่มีข้อมูลส่วนนี้</p>
      ) : (
        <p>
          {items.map((t, i) => (
            <span key={i}>{t}</span>
          ))}
        </p>
      )}
    </div>
  );
}

/* ================================================================
   กล่องยุบได้
   ================================================================ */

/**
 * ⚠ lazy = ไม่ประกอบเนื้อในจนกว่าจะถูกเปิดครั้งแรก แล้วคงไว้หลังจากนั้น
 *   ถ้าถอดออกทุกครั้งที่ยุบ การกดยุบ-กางจะยิงคำขอใหม่ทุกรอบ
 */
function Fold({
  title,
  summary,
  children,
  defaultOpen = false,
  lazy = false,
  nested = false,
}: {
  title: string;
  summary?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  lazy?: boolean;
  /** true = กล่องที่อยู่ข้างในกล่องอื่น — ลดน้ำหนักลงหนึ่งขั้นไม่ให้แย่งกับหัวกล่องแม่ */
  nested?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [touched, setTouched] = useState(defaultOpen);
  const id = useId();

  return (
    <section className={`pdt-fold${open ? ' is-open' : ''}${nested ? ' is-nested' : ''}`}>
      <button
        type="button"
        className="pdt-hd"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => {
          setOpen((v) => !v);
          setTouched(true);
        }}
      >
        <span className="pdt-caret" aria-hidden="true" />
        <span className="pdt-title">{title}</span>
        {/* สรุปยังอยู่ตอนกางด้วย ไม่ได้ซ่อน — ผู้ใช้จะได้ไม่ต้องยุบกลับเพื่อดูว่ากล่องนี้คืออะไร */}
        {summary && <span className="pdt-sum">{summary}</span>}
      </button>

      <div id={id} className="pdt-body" hidden={!open}>
        {(!lazy || touched) && children}
      </div>
    </section>
  );
}

/* ---------------- ตัวช่วย ---------------- */

/** แถวเดียวของตารางคู่ค่า — ค่าที่ไม่มีขึ้นขีดกลาง ไม่ใช่ซ่อนแถวทิ้ง */
function F({
  k,
  v,
  tone,
  wide,
}: {
  k: string;
  v: string | null | undefined;
  tone?: 'alert' | 'warn';
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'is-wide' : undefined}>
      <dt>{k}</dt>
      <dd className={tone ? `is-${tone}` : undefined}>{v && v.trim() ? v : '—'}</dd>
    </div>
  );
}

const TH_MONTH = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];

function thaiDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${Number(d)} ${TH_MONTH[Number(m) - 1]} ${Number(y) + 543}`;
}

function duration(months: number): string {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (!y) return `${m} เดือน`;
  return m ? `${y} ปี ${m} เดือน` : `${y} ปี`;
}
